import { z } from "zod";

// Analytics Types
export const analyticsDataSchema = z.object({
  activeUsers: z.number(),
  totalUsers: z.number(),
  newUsers: z.number(),
  activeUsersChange: z.number().optional(),
  totalUsersChange: z.number().optional(),
  newUsersChange: z.number().optional(),
  investorIntros: z.number().optional(),
  investorIntrosChange: z.number().optional(),
  totalAICost: z.number().optional(),
  totalAICostChange: z.number().optional(),
  dealstackReferences: z.number().optional(),
  dealstackReferencesChange: z.number().optional(),
  userSummary: z
    .object({
      bothFounderAndInvestor: z.number(),
      founder: z.number(),
      investor: z.number(),
    })
    .optional(),
  engagement: z
    .object({
      investorsAvgTime: z.string().optional(),
      foundersAvgTime: z.string().optional(),
    })
    .optional(),
});

export type TAnalyticsData = z.infer<typeof analyticsDataSchema>;

// Key Metrics Types
export const keyMetricsSchema = z.object({
  active_users: z.object({
    value: z.number(),
    trend_percentage: z.number(),
    trend_direction: z.enum(["up", "down", "neutral"]),
  }),
  investor_intros: z.object({
    value: z.number(),
    trend_percentage: z.number(),
    trend_direction: z.enum(["up", "down", "neutral"]),
  }),
  total_ai_cost: z.object({
    value: z.number(),
    trend_percentage: z.number(),
    trend_direction: z.enum(["up", "down", "neutral"]),
  }),
  dealstack_references: z.object({
    value: z.number(),
    trend_percentage: z.number(),
    trend_direction: z.enum(["up", "down", "neutral"]),
  }),
});

export type TKeyMetrics = z.infer<typeof keyMetricsSchema>;

// User Summary Types
export const userSummarySchema = z.object({
  new_users_count: z.number(),
  active_count: z.number(),
  inactive_count: z.number(),
  role_breakdown: z.object({
    both_founder_and_investor: z.number(),
    founder_only: z.number(),
    investor_only: z.number(),
  }),
});

export type TUserSummary = z.infer<typeof userSummarySchema>;

// Engagement Types
export const engagementWeeklyDataSchema = z.object({
  day: z.string(),
  investors_avg_time_minutes: z.number(),
  founders_avg_time_minutes: z.number(),
});

export const engagementSchema = z.object({
  investors_avg_time_minutes: z.number(),
  founders_avg_time_minutes: z.number(),
  weekly_data: z.array(engagementWeeklyDataSchema),
});

export type TEngagement = z.infer<typeof engagementSchema>;
export type TEngagementWeeklyData = z.infer<typeof engagementWeeklyDataSchema>;

// Cost Token Usage Types
export const costTokenUsageMonthlyDataSchema = z.object({
  month: z.string(),
  model_costs: z.record(z.string(), z.number()), // Model name -> cost mapping
  total_cost: z.number(),
});

export const costTokenUsageSchema = z.object({
  monthly_data: z.array(costTokenUsageMonthlyDataSchema),
  available_models: z.array(z.string()),
});

export type TCostTokenUsage = z.infer<typeof costTokenUsageSchema>;
export type TCostTokenUsageMonthlyData = z.infer<typeof costTokenUsageMonthlyDataSchema>;

// Company Summary Types
export const companySummaryMonthlyDataSchema = z.object({
  month: z.string(),
  founder_count: z.number(),
  investor_count: z.number(),
  both_count: z.number(),
  total_count: z.number(),
});

export const companySummarySchema = z.object({
  monthly_data: z.array(companySummaryMonthlyDataSchema),
});

export type TCompanySummary = z.infer<typeof companySummarySchema>;
export type TCompanySummaryMonthlyData = z.infer<typeof companySummaryMonthlyDataSchema>;

// Leaderboard Types
export const leaderboardCompanySchema = z.object({
  rank: z.number(),
  company_id: z.string(),
  company_name: z.string(),
  onboarding_score: z.number().nullable(),
  current_score: z.number().nullable(),
  trend: z.enum(["up", "down", "neutral"]),
  score_change: z.number(),
});

export const leaderboardSchema = z.object({
  companies: z.array(leaderboardCompanySchema),
  total_count: z.number(),
});

export type TLeaderboard = z.infer<typeof leaderboardSchema>;
export type TLeaderboardCompany = z.infer<typeof leaderboardCompanySchema>;

// Analytics Query Parameters
export type TAnalyticsQueryParams = {
  date_from?: string;
  date_to?: string;
  role_type?: "FUNDRAISER" | "INVESTOR";
  duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
};

export type TCostTokenUsageQueryParams = TAnalyticsQueryParams & {
  model_name?: string;
  duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
};

export type TLeaderboardQueryParams = {
  limit?: number;
  role_type?: "FUNDRAISER" | "INVESTOR";
  date_from?: string;
  date_to?: string;
  sort_by?: "current_score" | "onboarding_score" | "name";
  duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
};

export type TDownloadAnalyticsReportParams = {
  duration: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
  date_from?: string;
  date_to?: string;
  role_type?: "FUNDRAISER" | "INVESTOR";
  model_name?: string;
  sort_by?: "current_score" | "onboarding_score" | "name";
  leaderboard_limit?: number;
};

// User Management Types
export const roleResponseSchema = z.object({
  role_id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
});

export type TRoleResponse = z.infer<typeof roleResponseSchema>;

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  role: z.string(),
  status_id: z.number(),
  email_verified: z.boolean(),
  phone_verified: z.boolean(),
  two_factor_enabled: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  last_login: z.string().datetime().optional(),
  companies_count: z.number().optional(),
  chats_count: z.number().optional(),
  average_time_spent: z.string().optional(),
  average_time_spent_minutes: z.number().optional(),
  phone_number: z.string().optional(),
  roles: z.array(roleResponseSchema).optional(),
});

export type TAdminUser = z.infer<typeof adminUserSchema>;

export const usersListResponseSchema = z.object({
  users: z.array(adminUserSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number().optional(),
  per_page: z.number().optional(),
});

export type TUsersListResponse = z.infer<typeof usersListResponseSchema>;

/**
 * The admin users endpoint (PaginatedResponse) returns `data` as a BARE ARRAY
 * of users, while this legacy schema models `{ users: [...] }`. Accept both
 * shapes so consumers don't silently render empty lists.
 */
export function extractUsersList(payload: TUsersListResponse | TAdminUser[] | null | undefined): TAdminUser[] {
  if (!payload) return [];
  return Array.isArray(payload) ? payload : (payload.users ?? []);
}

export const updateUserRequestSchema = z.object({
  email: z.string().email().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  role: z.string().optional(),
  status_id: z.number().optional(),
});

export type TUpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

// System Configuration Types
// Backend groups settings by category; each field carries its own metadata.
export const systemConfigFieldSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  type: z.string(), // 'bool' | 'int' | 'string' | 'csv'
  editable: z.boolean(),
  description: z.string(),
  source: z.string(), // 'default' | 'override'
});

export type TSystemConfigField = z.infer<typeof systemConfigFieldSchema>;

export const systemConfigSchema = z.object({
  feature_flags: z.array(systemConfigFieldSchema),
  file_limits: z.array(systemConfigFieldSchema),
  verification: z.array(systemConfigFieldSchema),
  // 078: optional so a frontend deployed ahead of the backend keeps parsing.
  vetting: z.array(systemConfigFieldSchema).optional(),
  note: z.string().optional(),
});

export type TSystemConfig = z.infer<typeof systemConfigSchema>;

// PATCH body is a flat map of {key: value} for the editable keys being changed.
export type TUpdateSystemConfigRequest = Record<string, boolean | number | string>;

// GDPR request queue
export const gdprRequestSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  request_type: z.string(),
  status: z.string(),
  payload: z.record(z.unknown()).nullable().optional(),
  resolution_notes: z.string().nullable().optional(),
  requested_at: z.string(),
  resolved_at: z.string().nullable().optional(),
  resolved_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type TGdprRequest = z.infer<typeof gdprRequestSchema>;

export type TPaginatedGdprRequests = {
  items: TGdprRequest[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TGdprRequestsQueryParams = {
  status?: string;
  request_type?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export type TUpdateGdprRequest = {
  status?: string;
  resolution_notes?: string;
};

// Query Parameters
export type TUsersQueryParams = {
  page?: number;
  page_size?: number;
  search?: string;
  status_id?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

// Onboarding Questions Types
export const onboardingQuestionMetadataSchema = z
  .object({
    choices: z.array(z.string()).optional(),
    file_types: z.array(z.string()).optional(),
    max_file_size_mb: z.number().optional(),
    min_value: z.number().optional(),
    max_value: z.number().optional(),
  })
  .passthrough();

export type TOnboardingQuestionMetadata = z.infer<typeof onboardingQuestionMetadataSchema>;

export const onboardingQuestionSchema = z.object({
  id: z.string(),
  question_text: z.string(),
  question_description: z.string().optional().nullable(),
  data_type: z.enum([
    "text",
    "file_upload",
    "multiple_choice",
    "number",
    "text_or_file",
    "text_with_file",
    "file_with_text",
  ]),
  role_type: z.enum(["INVESTOR", "FUNDRAISER"]),
  order: z.number(),
  is_active: z.boolean(),
  metadata: onboardingQuestionMetadataSchema.nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type TOnboardingQuestion = z.infer<typeof onboardingQuestionSchema>;

export const onboardingQuestionsResponseSchema = z.object({
  data: z.array(onboardingQuestionSchema),
});

export type TOnboardingQuestionsResponse = z.infer<typeof onboardingQuestionsResponseSchema>;

export const createOnboardingQuestionRequestSchema = z.object({
  question_text: z.string().max(2000),
  question_description: z.string().optional(),
  data_type: z.enum([
    "text",
    "file_upload",
    "multiple_choice",
    "number",
    "text_or_file",
    "text_with_file",
    "file_with_text",
  ]),
  role_type: z.enum(["INVESTOR", "FUNDRAISER"]),
  order: z.number().optional(),
  is_active: z.boolean().optional(),
  metadata: onboardingQuestionMetadataSchema.nullable().optional(),
});

export type TCreateOnboardingQuestionRequest = z.infer<typeof createOnboardingQuestionRequestSchema>;

export const updateOnboardingQuestionRequestSchema = createOnboardingQuestionRequestSchema.partial();

export type TUpdateOnboardingQuestionRequest = z.infer<typeof updateOnboardingQuestionRequestSchema>;

export const reorderQuestionsRequestSchema = z.object({
  question_orders: z.record(z.string(), z.number()),
});

export type TReorderQuestionsRequest = z.infer<typeof reorderQuestionsRequestSchema>;

export type TOnboardingQuestionsQueryParams = {
  role_type?: "INVESTOR" | "FUNDRAISER";
  limit?: number;
  offset?: number;
  is_active?: boolean;
};

// Admin Promotion Types
export const promoteUserRequestSchema = z.object({
  email: z.string().email(),
  role_name: z.string(),
});

export type TPromoteUserRequest = z.infer<typeof promoteUserRequestSchema>;

export const promoteUserResponseSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  phone_number: z.string().optional(),
  status_id: z.number(),
  email_verified: z.boolean(),
  phone_verified: z.boolean(),
  roles: z.array(roleResponseSchema),
  permissions_count: z.number(),
});

export type TPromoteUserResponse = z.infer<typeof promoteUserResponseSchema>;

// Admin User Creation
export const createUserRequestSchema = z.object({
  first_name: z.string().min(1).max(64),
  last_name: z.string().min(1).max(64),
  email: z.string().email(),
  password: z.string().min(1).max(256),
  phone_number: z.string().min(10).max(20),
});

export type TCreateUserRequest = z.infer<typeof createUserRequestSchema>;

// Effective Permissions
export const adminPermissionSchema = z.object({
  permission_id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  resource: z.string(),
  action: z.string(),
});

export type TAdminPermission = z.infer<typeof adminPermissionSchema>;

export const effectivePermissionsSchema = z.object({
  permissions: z.array(adminPermissionSchema),
  computed_from_roles: z.array(z.string()),
});

export type TEffectivePermissions = z.infer<typeof effectivePermissionsSchema>;

// Tokens / Sessions
export const authTokenSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  token_type: z.string(),
  status: z.string(),
  expires_at: z.string().nullable().optional(),
  created_at: z.string(),
  last_used_at: z.string().nullable().optional(),
  ip_address: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
});

export type TAuthToken = z.infer<typeof authTokenSchema>;

export const refreshTokenSchema = z.object({
  token: z.string(),
  user_id: z.string(),
  expires_at: z.string(),
  created_at: z.string(),
});

export type TRefreshToken = z.infer<typeof refreshTokenSchema>;

export const userTokensSchema = z.object({
  auth_tokens: z.array(authTokenSchema),
  refresh_tokens: z.array(refreshTokenSchema),
});

export type TUserTokens = z.infer<typeof userTokensSchema>;

// Login Attempts
export const loginAttemptSchema = z.object({
  id: z.string(),
  user_id: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  ip_address: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  successful: z.boolean(),
  failure_reason: z.string().nullable().optional(),
  created_at: z.string(),
});

export type TLoginAttempt = z.infer<typeof loginAttemptSchema>;

export type TPaginatedLoginAttempts = {
  items: TLoginAttempt[];
  total_count: number;
  limit: number;
  offset: number;
};

// Audit Trail (admin/audit/users/{user_id})
export const adminAuditEventSchema = z.object({
  event_id: z.string().nullable().optional(),
  user_id: z.string().nullable().optional(),
  event_type: z.string(),
  timestamp: z.string(),
  target_entity: z.string().nullable().optional(),
  target_id: z.string().nullable().optional(),
  action_details: z.record(z.unknown()).nullable().optional(),
  ip_address: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  service_name: z.string().optional(),
  correlation_id: z.string().nullable().optional(),
});

export type TAdminAuditEvent = z.infer<typeof adminAuditEventSchema>;

export const userAuditTrailSchema = z.object({
  events: z.array(adminAuditEventSchema),
  total_count: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type TUserAuditTrail = z.infer<typeof userAuditTrailSchema>;

export type TUserAuditTrailQueryParams = {
  page?: number;
  page_size?: number;
};

// Audit Search / Recent / Stats (admin/audit/*)
export type TAuditSearchQueryParams = {
  user_id?: string;
  event_types?: string[];
  target_entity?: string;
  target_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

export const auditSearchResultSchema = z.object({
  events: z.array(adminAuditEventSchema),
  total_count: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export type TAuditSearchResult = z.infer<typeof auditSearchResultSchema>;

export const auditStatsSchema = z.object({
  total_events: z.number(),
  event_counts_by_type: z.record(z.number()),
  event_counts_by_entity: z.record(z.number()),
  top_users: z.array(
    z.object({
      user_id: z.string(),
      event_count: z.number(),
    })
  ),
  unique_users: z.number(),
  date_range: z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
});

export type TAuditStats = z.infer<typeof auditStatsSchema>;

export type TAuditStatsQueryParams = {
  date_from?: string;
  date_to?: string;
};

// Login Attempts listing / stats (admin/login-attempts/*)
export type TLoginAttemptsQueryParams = {
  user_id?: string;
  successful?: boolean;
  ip_address?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export type TFailedLoginAttemptsQueryParams = {
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export const loginStatsSchema = z.object({
  total_attempts: z.number(),
  successful_attempts: z.number(),
  failed_attempts: z.number(),
  success_rate: z.number(),
  unique_users: z.number(),
  unique_ips: z.number(),
  top_ips: z.array(z.record(z.unknown())),
});

export type TLoginStats = z.infer<typeof loginStatsSchema>;

export type TLoginStatsQueryParams = {
  date_from?: string;
  date_to?: string;
};

// Companies (admin/companies)
export const adminCompanySchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  role: z.enum(["FUNDRAISER", "INVESTOR"]),
  onboarding_completed: z.boolean(),
  onboarding_summary: z.string().nullable().optional(),
  summary_generation_status: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type TAdminCompany = z.infer<typeof adminCompanySchema>;

export type TPaginatedCompanies = {
  items: TAdminCompany[];
  total_count: number;
  limit: number;
  offset: number;
};

export const companySectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  role_type: z.enum(["FUNDRAISER", "INVESTOR"]).nullable().optional(),
  order: z.number(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type TCompanySection = z.infer<typeof companySectionSchema>;

export const companyStatsOverviewSchema = z.object({
  total_companies: z.number().optional(),
  companies_by_status: z.record(z.number()).optional(),
  companies_by_industry: z.record(z.number()).optional(),
  total_sections: z.number().optional(),
});

export type TCompanyStatsOverview = z.infer<typeof companyStatsOverviewSchema>;

export type TCompanyCountStats = Record<string, number>;

export type TCompaniesQueryParams = {
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  role?: string;
  onboarding_completed?: boolean;
};

// WhatsApp Introduction Groups
export type TWhatsAppGroupListItem = {
  id: string;
  name: string;
  whatsapp_group_id: string;
  invite_link: string;
  investor_company_name: string | null;
  fundraiser_company_name: string | null;
  created_at: string;
};

export type TPaginatedWhatsAppGroups = {
  items: TWhatsAppGroupListItem[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TWhatsAppGroupsQueryParams = {
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TWhatsAppGroupMessage = {
  id: string;
  content: string;
  direction: string;
  sender_phone: string | null;
  created_at: string | null;
};

export type TWhatsAppGroupDetail = {
  id: string;
  name: string;
  whatsapp_group_id: string;
  invite_link: string;
  investor_company_name: string | null;
  fundraiser_company_name: string | null;
  created_at: string;
  messages: TWhatsAppGroupMessage[];
};

// ── Phase 3: RBAC, reviewers, onboarding answers, chat moderation, sections ──

// Roles & Permissions
export type TAdminRole = {
  role_id: string;
  name: string;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TAdminPermissionDetail = {
  permission_id: string;
  name: string;
  description?: string | null;
  resource: string;
  action: string;
};

export type TRoleUser = {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  status_id?: number | null;
  role?: string | null;
};

export type TPaginatedRoleUsers = {
  items: TRoleUser[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TCreateRoleRequest = {
  name: string;
  description?: string | null;
};

export type TUpdateRoleRequest = {
  name?: string | null;
  description?: string | null;
};

export type TCreatePermissionRequest = {
  name: string;
  description?: string | null;
  resource: string;
  action: string;
};

export type TUpdatePermissionRequest = {
  name?: string | null;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
};

// Reviewers
export type TReviewer = {
  user_id: string;
  is_reviewer?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

// Onboarding answers & files
export type TOnboardingAnswer = {
  id: string;
  company_id: string;
  question_id: string;
  answer_text?: string | null;
  answer_number?: number | null;
  answer_choice?: string | null;
  answer_file_url?: string | null;
  status?: string | null;
  created_at?: string | null;
  has_file?: boolean;
  has_text?: boolean;
  file_metadata?: Record<string, unknown> | null;
};

export type TOnboardingAnswersQueryParams = {
  company_id?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

// Paginated envelope for the four GROUP B endpoints converted to
// { items, total_count, limit, offset }.
export type TPaginatedEnvelope<T> = {
  items: T[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TPaginatedOnboardingAnswers = TPaginatedEnvelope<TOnboardingAnswer>;

export type TOnboardingStatsOverview = {
  total_questions?: number;
  total_answers?: number;
  completion_rates?: Record<string, unknown>;
  answers_by_type?: Record<string, unknown>;
};

export type TAdminFile = {
  id: string;
  storage_provider?: string;
  storage_path?: string;
  original_filename: string;
  file_size?: number;
  content_type?: string;
  file_hash?: string;
  uploaded_by?: string;
  company_id?: string | null;
  file_type?: string;
  is_public?: boolean;
  status?: string;
  file_metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

// Chat moderation
export type TChatThread = {
  id: string;
  company_id: string;
  section_id: string;
  title?: string | null;
  status?: string | null;
  thread_metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TChatMessage = {
  id: string;
  thread_id: string;
  sender_id?: string | null;
  message_type?: string | null;
  content: string;
  attachments?: Record<string, unknown>[] | null;
  ai_metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TChatThreadsQueryParams = {
  company_id?: string;
  section_id?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export type TPaginatedChatThreads = TPaginatedEnvelope<TChatThread>;

export type TChatStatsOverview = {
  total_threads?: number;
  total_messages?: number;
  active_threads?: number;
  threads_by_status?: Record<string, unknown>;
  messages_by_type?: Record<string, unknown>;
};

export type TChatCategory = {
  id: string;
  section_id: string;
  name: string;
  description?: string | null;
  category_order?: number;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TCreateChatCategoryRequest = {
  section_id: string;
  name: string;
  description?: string | null;
  category_order?: number;
  is_active?: boolean;
};

export type TUpdateChatCategoryRequest = {
  section_id?: string;
  name?: string | null;
  description?: string | null;
  category_order?: number | null;
  is_active?: boolean | null;
};

// Sections
export type TAdminSection = {
  id: string;
  name: string;
  description?: string | null;
  role_type?: "FUNDRAISER" | "INVESTOR" | null;
  order?: number;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TCreateSectionRequest = {
  name: string;
  description?: string | null;
  role_type?: "FUNDRAISER" | "INVESTOR" | null;
  order?: number;
};

export type TUpdateSectionRequest = {
  name?: string | null;
  description?: string | null;
  role_type?: "FUNDRAISER" | "INVESTOR" | null;
  order?: number | null;
  is_active?: boolean | null;
};

// ── WhatsApp Templates (admin/whatsapp-templates/*) ──
export type TWhatsAppTemplate = {
  id: string;
  name: string;
  category?: string;
  language?: string;
  status?: string;
  components?: Record<string, unknown>;
  whatsapp_template_id?: string | null;
  is_active?: boolean;
  template_key?: string | null;
  lifecycle_status?: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TUpdateWhatsAppTemplateRequest = {
  category?: string | null;
  language?: string | null;
  status?: string | null;
  components?: Record<string, unknown> | null;
  whatsapp_template_id?: string | null;
  is_active?: boolean | null;
  template_key?: string | null;
  lifecycle_status?: string | null;
};

// ── WhatsApp Analytics (admin/whatsapp-analytics/*) ──
export type TWhatsAppAnalyticsSummary = {
  commands_by_name?: Record<string, number>;
  commands_by_day?: Record<string, number>;
  reply_path_mix?: Record<string, number>;
  messages_by_company?: { company_id: string; message_count: number }[];
  outbound_message_types?: {
    session?: number;
    template?: number;
    template_vs_session_ratio?: number;
  };
};

export type TWhatsAppAnalyticsQueryParams = {
  duration?: "24h" | "7d" | "30d";
  company_limit?: number;
};

// ── AI usage (admin/ai/*) ──
export type TAiUsageStats = {
  total_requests?: number;
  successful_requests?: number;
  failed_requests?: number;
  success_rate?: number;
  average_processing_time?: number;
  requests_today?: number;
  requests_this_week?: number;
  requests_this_month?: number;
  total_tokens?: number;
  total_cost_usd?: number;
  average_response_time_ms?: number;
};

export type TAiModelStats = {
  models_used?: unknown[];
  model_performance?: Record<string, unknown>;
  model_costs?: Record<string, unknown>;
  model_usage_counts?: Record<string, unknown>;
};

export type TAiChatLog = {
  id: string;
  ai_provider: string;
  total_cost?: number | null;
  total_tokens?: number | null;
  thread_id?: string | null;
  was_successful: boolean;
  error_message?: string | null;
  created_at?: string | null;
};

export type TAiUsageLogsQueryParams = {
  company_id?: string;
  model_name?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export type TPaginatedAiUsageLogs = TPaginatedEnvelope<TAiChatLog>;

export type TAiContext = {
  id: string;
  thread_id?: string;
  context_summary?: string;
  context_version?: number;
  last_updated?: string;
  created_at?: string;
};

// ── Files admin (admin/files/*) ──
export type TFilesQueryParams = {
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
};

export type TPaginatedFiles = TPaginatedEnvelope<TAdminFile>;

export type TFileStatsOverview = {
  total_files?: number;
  total_size_bytes?: number;
  files_by_type?: Record<string, number>;
  files_by_user?: Record<string, number>;
};

// ── Deal Network (admin/deal-network/*) ──
export type TInvestorPlatformInvestor = {
  id: string;
  investor_platform_id: string;
  company_name?: string | null;
  contact_name?: string | null;
  website?: string | null;
  contact_us_link?: string | null;
  email?: string | null;
  telephone?: string | null;
  dealroom_investor_id?: number | null;
  dealroom_json_response?: Record<string, unknown> | null;
  company_id?: string | null;
  platform?: {
    id: string;
    name: string;
    website: string;
    contact_us_link: string;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TPaginatedInvestorPlatformInvestors = {
  items: TInvestorPlatformInvestor[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TInvestorPlatformInvestorsQueryParams = {
  investor_platform_id?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TCreateInvestorPlatformInvestorRequest = {
  investor_platform_id: string;
  company_name?: string | null;
  contact_name?: string | null;
  website?: string | null;
  contact_us_link?: string | null;
  email?: string | null;
  telephone?: string | null;
  dealroom_investor_id?: number | null;
  dealroom_json_response?: Record<string, unknown> | null;
  company_id?: string | null;
};

export type TUpdateInvestorPlatformInvestorRequest = {
  investor_platform_id?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  website?: string | null;
  contact_us_link?: string | null;
  email?: string | null;
  telephone?: string | null;
  dealroom_investor_id?: number | null;
  dealroom_json_response?: Record<string, unknown> | null;
  company_id?: string | null;
};

export type TConnectRequestCompany = {
  id?: string;
  name?: string;
  [key: string]: unknown;
};

export type TConnectRequest = {
  id: string;
  company_recommendation_id: string;
  status: { id: string; name: string };
  created_at?: string | null;
  updated_at?: string | null;
  fundraiser_company?: TConnectRequestCompany | null;
  investor_company?: TConnectRequestCompany | null;
};

export type TPaginatedConnectRequests = {
  items: TConnectRequest[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TConnectRequestsQueryParams = {
  status?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TUpdateConnectRequestStatusRequest = {
  status: string;
};
